import React, { useMemo } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { IconExternalLink } from '@tabler/icons'
import { Grid } from '@mantine/core'
import useStyles from './SpotifyIsntLegal.styles'
import { translate, isEnglish } from '../../../../utilities/language'
import { getAttribution } from '../../../../utilities/content'
import {
  ARTICLE_IMAGE_AVIF,
  ARTICLE_IMAGE_JPEG,
  SPOTIFY_ISNT_LEGAL_TITLE_HTML,
  SPOTIFY_ISNT_LEGAL_TITLE_HTML_ES,
  SPOTIFY_ISNT_LEGAL_TITLE_HTML_FR,
  SPOTIFY_ISNT_LEGAL_TITLE_HTML_KO,
  SPOTIFY_ISNT_LEGAL_ARTICLE_PUBLISHED_STR,
} from '../../../../static-data/spotify-isnt-legal-for-your-business-data'
import {
  MAIN_SITE_ENVIRONMENT_HOST,
  MAIN_SITE_ENVIRONMENT_HOST_ES,
  MAIN_SITE_ENVIRONMENT_HOST_FR,
  MAIN_SITE_ENVIRONMENT_HOST_KO,
} from '../../../../utilities/config'

const BackToArticle: React.ComponentType<any> = dynamic(
  () => import('../../../../components/controls/BackToArticle'),
  {
    ssr: true,
  }
)
const ReadyToTransform: React.ComponentType<any> = dynamic(
  () => import('../../../../components/controls/ReadyToTransform'),
  {
    ssr: true,
  }
)

export default function SpotifyIsntLegal(props: any) {
  const { classes } = useStyles()
  const selectedLanguage: any = props?.selectedLanguage
  const width: any = props?.width
  const avifCompatible = props?.avifCompatible

  const articleImage = useMemo(() => {
    return (
      <Image
        width={512}
        height={279}
        sizes="(max-width: 350px) 256px, 
               (max-width: 430px) 300px, 
               (max-width: 590px) 384px, 
               512px"
        src={avifCompatible ? ARTICLE_IMAGE_AVIF : ARTICLE_IMAGE_JPEG}
        alt=""
        priority={true}
        style={{
          marginTop: '30px',
          borderRadius: '10px',
          display: 'block',
          width: '100%',
          maxWidth: '512px',
          height: 'auto',
        }}
      />
    )
  }, [avifCompatible])

  return (
    <div className={classes.mainGrid}>
      <div className={classes.innerDiv}>
        <Grid columns={24}>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <h1 className={classes.standardText + ' ' + classes.h1Tag}>
                {translate(
                  SPOTIFY_ISNT_LEGAL_TITLE_HTML,
                  SPOTIFY_ISNT_LEGAL_TITLE_HTML_ES,
                  SPOTIFY_ISNT_LEGAL_TITLE_HTML_FR,
                  SPOTIFY_ISNT_LEGAL_TITLE_HTML_KO,
                  selectedLanguage
                )}
              </h1>
              {getAttribution(
                translate(
                  'Editor',
                  'Editora',
                  'Éditrice',
                  '편집자',
                  selectedLanguage
                ),
                SPOTIFY_ISNT_LEGAL_ARTICLE_PUBLISHED_STR,
                selectedLanguage,
                false
              )}
              {isEnglish(selectedLanguage) && articleImage}
            </div>
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <h2 className={classes.pParagraph}>
              {translate(
                <>
                  Every day, thousands of business owners make the same
                  expensive mistake: they hit play on Spotify Premium in their
                  coffee shop, boutique, or gym, assuming that their monthly
                  subscription includes commercial use.
                </>,
                <>
                  Cada día, miles de empresarios hacen lo mismo error costoso:
                  le dieron al play en Spotify Premium en su cafetería, boutique
                  o gimnasio, asumiendo que su pago mensual la suscripción
                  incluye el uso comercial.
                </>,
                <>
                  Chaque jour, des milliers de propriétaires d'entreprise
                  commettent la même erreur coûteuse : ils jouent sur Spotify
                  Premium dans leur café, boutique ou salle de sport, en
                  supposant que leur abonnement mensuel inclut une utilisation
                  commerciale.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    매일
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    수천
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    명의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사업주가
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    똑같은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    일을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    하고
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    있습니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    값비싼
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    실수:
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그들은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    Spotify
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    Premium에서
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    플레이를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    시작했습니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    커피숍,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    부티크
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    또는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    체육관에서
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    매월
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    구독에는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    상업적
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사용이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    포함됩니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </h2>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  It doesn’t. And it could cost you up to{' '}
                  <span className={classes.spanEmphasis}>
                    $150,000 per song.
                  </span>
                </>,
                <>
                  No es así. Y podría costarte hasta{' '}
                  <span className={classes.spanEmphasis}>
                    150.000 dólares por canción.
                  </span>
                </>,
                <>
                  Ce n'est pas le cas. Et cela pourrait vous coûter jusqu'à{' '}
                  <span className={classes.spanEmphasis}>
                    150&nbsp;000&nbsp;$ par chanson.
                  </span>
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그렇지
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    않습니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그리고
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    노래당
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    최대
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    $150,000의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    비용이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    들
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    수
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    있습니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  This nightmare scenario happened in 2019 at XY Bar in Wichita
                  when performing rights organizations sued them due to playing
                  unlicensed music. They’re not alone. ASCAP filed lawsuits
                  against Chicago bars Fatpour in Wicker Park and Kirkwood Bar
                  in Lakeview, seeking $150,000 in damages&nbsp;— despite annual
                  licenses costing just $2,800 and $1,100, respectively.
                </>,
                <>
                  Este escenario de pesadilla ocurrió en 2019 en XY Bar en
                  Wichita cuando las organizaciones de derechos de
                  interpretación los demandaron por jugar música sin licencia.
                  No están solos. ASCAP presentó demandas contra los bares de
                  Chicago Fatpour en Wicker Park y Kirkwood Bar en Lakeview,
                  solicitando $150,000 en daños y perjuicios, a pesar de las
                  licencias cuestan sólo $2,800 y $1,100, respectivamente.
                </>,
                <>
                  Ce scénario cauchemardesque s'est produit en 2019 au XY Bar à
                  Wichita lorsque des organisations de droits d'exécution les
                  ont poursuivis en justice pour avoir joué musique sans
                  licence. Ils ne sont pas seuls. L'ASCAP a porté plainte contre
                  les bars de Chicago Fatpour à Wicker Park et Kirkwood Bar à
                  Lakeview, réclamant 150&nbsp;000&nbsp;$ de dommages et
                  intérêts – malgré des licences ne coûtant que
                  2&nbsp;800&nbsp;$ et 1 100&nbsp;$, respectivement.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    악몽
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    같은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    시나리오는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    2019년
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    위치타의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    XY
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    Bar에서
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    공연
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    권리
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    단체가
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    무면허
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    재생을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    이유로
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    고소했을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    때
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    일어났습니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그들은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    혼자가
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    아닙니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    ASCAP는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    연간
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    라이센스
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    비용이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    각각
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    $2,800와
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    $1,100에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    불과함에도
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    불구하고
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    Wicker
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    Park의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    시카고
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    바
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    Fatpour와
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    Lakeview의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    Kirkwood
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    Bar에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    대해
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    $150,000의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    손해
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    배상을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    요구하는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    소송을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    제기했습니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  The difference between what business owners believe to be
                  legal and what is actually legal has turned out to be a
                  million-dollar trap. Let’s break down why Spotify’s business
                  license isn’t what you think it is&nbsp;— and what you ought
                  to do.
                </>,
                <>
                  La diferencia entre lo que los dueños de negocios creen que es
                  legal y lo que es realmente legal ha resultado ser una trampa
                  del millón de dólares. Analicemos por qué el negocio de
                  Spotify La licencia no es lo que crees que es, y lo que
                  deberías hacer.
                </>,
                <>
                  La différence entre ce que pensent les propriétaires
                  d’entreprise légal et ce qui est réellement légal s'est avéré
                  être un piège à un million de dollars. Voyons pourquoi
                  l'activité de Spotify la licence n’est pas ce que vous
                  pensez&nbsp;– et ce que vous devriez faire.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사업주들이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    합법적이라고
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    믿는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것과
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    실제로
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    합법적인
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    차이는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    백만
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    달러의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    함정임이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    밝혀졌습니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    Spotify의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    비즈니스
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    라이선스가
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    귀하가
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    생각하는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것과
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    왜
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    다른지,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그리고
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    무엇을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    해야
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    하는지
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    분석해
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    보겠습니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <section id={'can-i-use-spotify-in-my-store'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>The Hard Truth: Can I Use Spotify in My Store?</>,
                  <>La dura verdad: ¿puedo usar Spotify en mi tienda?</>,
                  <>
                    La dure vérité&nbsp;: puis-je utiliser Spotify dans ma
                    boutique&nbsp;?
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어려운
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      진실:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      매장에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있나요?
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>Short answer: Absolutely not.</>,
                  <>Respuesta corta: Absolutamente no.</>,
                  <>Réponse courte&nbsp;: Absolument pas.</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      짧은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대답:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      절대
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그렇지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    It seems instinctive, but when you pay for a subscription to
                    Spotify Premium, it does not give you the right to use music
                    commercially. Here’s what Spotify’s Terms of Service
                    actually say:
                  </>,
                  <>
                    Parece instintivo, pero cuando pagas una suscripción a
                    Spotify Premium, no te da derecho a usar música
                    comercialmente. Esto es lo que realmente son los Términos de
                    servicio de Spotify decir:
                  </>,
                  <>
                    Cela semble instinctif, mais lorsque vous payez un
                    abonnement à Spotify Premium, il ne vous donne pas le droit
                    d'utiliser de la musique commercialement. Voici quelles sont
                    réellement les conditions d’utilisation de Spotify dire:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      본능적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      들리겠지만,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Premium
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      구독료를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지불한다고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      해서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상업적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      권리가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부여되는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아닙니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      서비스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      약관에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실제로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      명시된
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      내용은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다음과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      같습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    “Subject to your compliance with these Terms, we grant to
                    you a limited, non-exclusive, revocable license to make
                    personal, non-commercial use of the Spotify Service.”
                  </>,
                  <>
                    “Sujeto a su cumplimiento de estos Términos, le otorgamos
                    una licencia limitada, no exclusiva y revocable para
                    realizar actividades personales, uso no comercial del
                    Servicio Spotify”.
                  </>,
                  <>
                    «&nbsp;Sous réserve de votre respect des présentes
                    Conditions, nous vous accordons une licence limitée, non
                    exclusive et révocable pour rendre personnel, utilisation
                    non commerciale du service Spotify.&nbsp;»
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      “귀하의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      본
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      약관
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      준수에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따라
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      당사는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      귀하에게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      서비스를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      개인적,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비상업적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      용도로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제한적이고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비독점적이며
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      취소
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가능한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부여합니다.”
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>Let’s decode that legal language:</>,
                  <>escifremos ese lenguaje legal:</>,
                  <>Descifremos ese lenguaje legal:</>,
                  <>해당 법적 언어를 해독해 보겠습니다:</>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>"Personal"&nbsp;= You, alone, listening</>,
                    <>"Personale"&nbsp;= Tu, sola, escuchando</>,
                    <>"Personnel"&nbsp;= Vous, seul, à l'écoute</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        "개인적"
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        =
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        당신이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        혼자
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        듣고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      "Non-commercial"&nbsp;= Not in any business setting where
                      customers can&nbsp;hear&nbsp;it
                    </>,
                    <>
                      "No comercial"&nbsp;= No en ningún entorno empresarial
                      donde los clientes pueden escucharlo
                    </>,
                    <>
                      "Non commerciale"&nbsp;= Pas dans un contexte
                      professionnel où les clients peuvent l'entendre
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        "비상업적"
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        =
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고객이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        들을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비즈니스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        환경이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        아님
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>"Limited"&nbsp;= Extremely restricted in scope</>,
                    <>"Limitada"&nbsp;= alcance extremadamente restringido</>,
                    <>"Limité"&nbsp;= Portée extrêmement restreinte</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        "비상업적"
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        =
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고객이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        들을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비즈니스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        환경이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        아님
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
            </section>
            <section id={'what-about-spotify-for-business'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>What About “Spotify for Business”?</>,
                  <>¿Qué pasa con “Spotify para empresas”?</>,
                  <>
                    Qu’en est-il de «&nbsp;Spotify pour les
                    entreprises&nbsp;»&nbsp;?
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      "비즈니스용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify"는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어떻습니까?
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Despite popular belief, Spotify does not offer a Spotify for
                    Business or Spotify Pro service. If you search for “Spotify
                    for Business” in the search engine, you won’t find an
                    official solution because it does not exist.
                  </>,
                  <>
                    A pesar de la creencia popular, Spotify no ofrece Spotify
                    para Servicio Business o Spotify Pro. Si buscas “Spotify for
                    Business” en el buscador, no encontrarás una oficial
                    solución porque no existe.
                  </>,
                  <>
                    Malgré la croyance populaire, Spotify ne propose pas de
                    Spotify pour Service Entreprise ou Spotify Pro. Si vous
                    recherchez «&nbsp;Spotify for Business&nbsp;» dans le moteur
                    de recherche, vous ne trouverez pas de responsable solution
                    car elle n’existe pas.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대중적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      믿음에도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      불구하고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      for
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Business
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      또는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Pro
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      서비스를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제공하지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      검색엔진에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      "Spotify
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      for
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Business"를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      검색하시면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공식
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      솔루션이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      존재하지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      때문에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      찾을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      없습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>The same limitations are valid for:</>,
                  <>Las mismas limitaciones son válidas para:</>,
                  <>Les mêmes limitations sont valables pour&nbsp;:</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다음에도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동일한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제한이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적용됩니다:
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>Apple Music</li>
                <li className={classes.liArticle}>Amazon Music</li>
                <li className={classes.liArticle}>
                  Pandora{' '}
                  {translate(
                    <>(personal accounts)</>,
                    <>(cuentas personales)</>,
                    <>(comptes personnels)</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        (개인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        계정)
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>YouTube Music</li>
                <li className={classes.liArticle}>Tidal</li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Any streaming service to consumers.</>,
                    <>Cualquier servicio de streaming para consumidores.</>,
                    <>Tout service de streaming pour les consommateurs.</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소비자를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        위한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        모든
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스트리밍
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        서비스.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Research indicates that 80% of business owners are not aware
                    that music services are not licensed to be used
                    commercially. If you’re reading this with a sinking feeling
                    in your stomach, you’re not alone.
                  </>,
                  <>
                    Las investigaciones indican que el 80% de los propietarios
                    de empresas no lo saben que los servicios de música no
                    tienen licencia para su uso comercial. Si estás leyendo esto
                    con una sensación de hundimiento en el estómago, no estás
                    solo.
                  </>,
                  <>
                    Des recherches indiquent que 80&nbsp;% des propriétaires
                    d'entreprise ne le savent pas. que les services de musique
                    ne sont pas autorisés à des fins commerciales. Si vous lisez
                    ceci avec une sensation de serrement d'estomac, tu n'es pas
                    seul.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연구에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따르면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사업주
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      중
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      80%는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      서비스가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상업적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용되도록
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      허가되지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않았다는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사실을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모르고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      속이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가라앉는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      느낌으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      글을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      읽고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      계시다면,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      당신은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      혼자가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아닙니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisor} />
            <section id={'example-lawsuit-against-peloton'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>Real-World Example: Peloton’s $49 Million Lesson</>,
                  <>
                    Ejemplo del mundo real: la lección de 49 millones de dólares
                    de Peloton
                  </>,
                  <>
                    Exemple concret&nbsp;: la leçon de 49 millions de dollars de
                    Peloton
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실제
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사례:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Peloton의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      4,900만
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      달러
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      교훈
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    You think you’re too big to be caught? Think again. In March
                    2019, the National Music Publishers Association sued Peloton
                    in a lawsuit worth $150 million in damages, later increasing
                    this to $300 million in an amended complaint after it was
                    found that they used their music extensively without a
                    license.
                  </>,
                  <>
                    ¿Crees que eres demasiado grande para que te atrapen? Piensa
                    de nuevo. en marzo 2019, la Asociación Nacional de Editores
                    de Música demandó a Peloton en una demanda por valor de 150
                    millones de dólares en daños, que luego aumentó esto a $300
                    millones en una demanda enmendada después de que fue
                    descubrió que usaban su música extensamente sin un licencia.
                  </>,
                  <>
                    Vous pensez que vous êtes trop gros pour être attrapé&nbsp;?
                    Détrompez-vous. En mars 2019, la National Music Publishers
                    Association a poursuivi Peloton dans un procès d'une valeur
                    de 150 millions de dollars en dommages et intérêts,
                    augmentant par la suite cela à 300 millions de dollars dans
                    une plainte modifiée après qu'elle ait été ont constaté
                    qu'ils utilisaient beaucoup leur musique sans licence.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      잡히기에는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      너무
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      크다고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생각하시나요?
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다시
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생각해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보세요.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      2019년
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      3월,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      국립
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      출판사
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      협회는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Peloton을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      1억
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      5천만
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      달러
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      규모의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      손해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      배상
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소송으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      고소했으며,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      나중에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이센스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      없이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      자신의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      광범위하게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용했다는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사실이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      밝혀진
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      후
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수정된
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      불만
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사항을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      통해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      금액을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      3억
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      달러로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      늘렸습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Peloton Interactive incurred settlement and litigation costs
                    of $49.3 million to settle a copyright suit filed by music
                    publishers.
                  </>,
                  <>
                    Peloton Interactive incurrió en costos de liquidación y
                    litigio de 49,3 millones de dólares para resolver una
                    demanda por derechos de autor presentada por la música
                    editores.
                  </>,
                  <>
                    Peloton Interactive a engagé des frais de règlement et de
                    litige de 49,3 millions de dollars pour régler une poursuite
                    en matière de droits d'auteur intentée par la musique
                    éditeurs.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Peloton
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Interactive는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음반
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      출판사가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제기한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      저작권
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소송을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      해결하기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      위해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      4,930만
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      달러의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      및
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소송
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비용을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      발생시켰습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Peloton is a billion-dollar organization with huge legal
                    capacities. What chance does a small café or boutique gym
                    have?
                  </>,
                  <>
                    Peloton es una organización de mil millones de dólares con
                    enormes riesgos legales. capacidades. ¿Qué posibilidades
                    tiene una pequeña cafetería o un gimnasio boutique? ¿tener?
                  </>,
                  <>
                    Peloton est une organisation d'un milliard de dollars avec
                    d'énormes capacités juridiques capacités. Quelle chance a un
                    petit café ou une salle de sport avoir?
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Peloton은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      막대한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      법적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      역량을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      갖춘
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수십억
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      달러
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      규모의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      조직입니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      카페나
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부티크
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      체육관에는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어떤
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기회가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있을까요?
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id={'how-businesses-get-caught'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>How Businesses Actually Get Caught</>,
                  <>Cómo quedan realmente atrapadas las empresas</>,
                  <>Comment les entreprises se font réellement prendre</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기업이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실제로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      잡히는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      방법
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    PROs (Performing Rights Organizations) don’t wait for
                    complaints. They actively monitor businesses through:
                  </>,
                  <>
                    Las PRO (organizaciones de derechos de interpretación) no
                    esperan quejas. Supervisan activamente las empresas a través
                    de:
                  </>,
                  <>
                    Les PRO (Organisations de Droits d’Exécution) n’attendent
                    pas plaintes. Ils surveillent activement les entreprises à
                    travers&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      PRO(공연권
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      단체)는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      불만사항을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기다리지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그들은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다음을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      통해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비즈니스를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적극적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모니터링합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' check-bullets'}>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      Field representatives who make unannounced visits to
                      establishments.
                    </>,
                    <>
                      Representantes de campo que realizan visitas sin previo
                      aviso a establecimientos.
                    </>,
                    <>
                      Les représentants sur le terrain qui effectuent des
                      visites inopinées établissements.
                    </>,
                    <>예고 없이 시설을 방문하는 현장 대표자.</>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      Monitoring of social media for business activities and
                      promotion.
                    </>,
                    <>
                      Seguimiento de las redes sociales para actividades
                      empresariales y promoción.
                    </>,
                    <>
                      Surveillance des médias sociaux pour les activités
                      commerciales et promotion.
                    </>,
                    <>비즈니스 활동 및 홍보를 위한 소셜 미디어 모니터링.</>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Tips, customer complaints.</>,
                    <>Propinas, quejas de clientes.</>,
                    <>Pourboires, réclamations clients.</>,
                    <>팁, 고객 불만.</>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Cross-checking of licensing database.</>,
                    <>Verificación cruzada de la base de datos de licencias.</>,
                    <>
                      Vérification croisée de la base de données des licences.
                    </>,
                    <>라이센스 데이터베이스의 교차 점검.</>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Digital tracking of music usage.</>,
                    <>Seguimiento digital del uso de la música.</>,
                    <>Suivi numérique de l'utilisation de la musique.</>,
                    <>음악 사용의 디지털 추적.</>,
                    selectedLanguage
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    When a PRO finds a violation, they contact businesses via
                    phone, email, and mail. If ignored, they send a “Cease &
                    Desist Notice” with five days to respond&nbsp;— after which
                    they sue.
                  </>,
                  <>
                    Cuando un PRO encuentra una infracción, se comunica con las
                    empresas a través de teléfono, correo electrónico y correo.
                    Si se ignora, envían un “Cesar y Aviso de desistimiento” con
                    cinco días para responder, después de lo cual ellos
                    demandan.
                  </>,
                  <>
                    Lorsqu'un PRO constate une violation, il contacte les
                    entreprises via téléphone, e-mail et courrier. S’ils sont
                    ignorés, ils envoient un «&nbsp;Cease & Avis de
                    désistement&nbsp;» avec cinq jours pour répondre&nbsp;—
                    après quoi ils poursuivre en justice.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      PRO는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      위반
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사항을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      발견하면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전화,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이메일,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      우편을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      통해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기업에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연락합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      무시할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      경우
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      응답
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      5일
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이내에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      "중단
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      및
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      단념
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      통지"를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보낸
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      후
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소송을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제기합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id={'understanding-music-licensing'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>Understanding Music Licensing: Why It’s Complicated</>,
                  <>
                    Comprender las licencias de música: por qué son complicadas
                  </>,
                  <>
                    Comprendre les licences musicales&nbsp;: pourquoi c'est
                    compliqué
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이해:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      복잡한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이유
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Understanding why Spotify can’t be used for in-store music
                    begins with knowing the fundamentals of music licensing.
                  </>,
                  <>
                    Comprender por qué Spotify no se puede utilizar para la
                    música en las tiendas comienza conociendo los fundamentos de
                    las licencias musicales.
                  </>,
                  <>
                    Comprendre pourquoi Spotify ne peut pas être utilisé pour la
                    musique en magasin commence par connaître les principes
                    fondamentaux des licences musicales.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      매장
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      내
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      없는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이유를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이해하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기본
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사항을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      시작됩니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>There are two types of copyright in every song:</>,
                  <>Hay dos tipos de derechos de autor en cada canción:</>,
                  <>
                    Il existe deux types de droits d'auteur sur chaque
                    chanson&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      노래에는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      두
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      유형의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      저작권이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id={'composition-rights'}>
              <h3 className={classes.pParagraph}>
                <span className={classes.spanEmphasis}>
                  {translate(
                    <>1. Composition Rights (The Song Itself)</>,
                    <>1. Derechos de composición (la canción en sí)</>,
                    <>1. Droits de composition (la chanson elle-même)</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        1.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작곡권(곡
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자체)
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </span>
              </h3>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Owned by publishers, composers, and songwriters. This covers
                    the music, lyrics, and composition.
                  </>,
                  <>
                    Propiedad de editores, compositores y compositores. Esto
                    cubre la música, letra y composición.
                  </>,
                  <>
                    Propriété d'éditeurs, de compositeurs et
                    d'auteurs-compositeurs. Cela couvre la musique, les paroles
                    et la composition.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      출판사,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작곡가,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작곡가가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소유합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      여기에는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가사,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작곡이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      포함됩니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id={'sound-recording-rights'}>
              <h3 className={classes.pParagraph}>
                <span className={classes.spanEmphasis}>
                  {translate(
                    <>2. Sound Recording Rights (The Specific Recording)</>,
                    <>
                      2. Derechos de grabación sonora (la grabación específica)
                    </>,
                    <>
                      2. Droits d'enregistrement sonore (l'enregistrement
                      spécifique)
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        2.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        녹음
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        권리(특정
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        녹음)
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </span>
              </h3>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Owned by record labels and performers. This includes the
                    specific version of the song that you are listening to.
                  </>,
                  <>
                    Propiedad de sellos discográficos e intérpretes. Esto
                    incluye el versión específica de la canción que estás
                    escuchando.
                  </>,
                  <>
                    Propriété de maisons de disques et d'artistes interprètes.
                    Cela inclut le version spécifique de la chanson que vous
                    écoutez.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음반사와
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공연자가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소유합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      여기에는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      듣고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      노래의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      특정
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      버전이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      포함됩니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    When you play music publicly, you need licenses for BOTH.
                  </>,
                  <>
                    Cuando reproduces música públicamente, necesitas licencias
                    para AMBOS.
                  </>,
                  <>
                    Lorsque vous écoutez de la musique en public, vous avez
                    besoin de licences pour les DEUX.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공개적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재생하려면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      두
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모두에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      필요합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id={'the-four-major-pros-in-america'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>The Four Major PROs in the U.S.</>,
                  <>Los cuatro principales PRO en EE. UU.</>,
                  <>Les quatre principaux PRO aux États-Unis</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      미국의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      4대
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      PRO
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    These companies collect and dispense the royalties on
                    composition rights:
                  </>,
                  <>
                    Estas empresas recaudan y distribuyen las regalías sobre
                    derechos de composición:
                  </>,
                  <>
                    Ces sociétés perçoivent et distribuent les redevances sur
                    droits de composition&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이들
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      회사는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작곡
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      권리에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      로열티를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      징수하고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      분배합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  ASCAP (American Society of Composers, Authors and Publishers)
                </li>
                <li className={classes.liArticle}>
                  BMI (Broadcast Music, Inc.)
                </li>
                <li className={classes.liArticle}>
                  SESAC (Society of European Stage Authors and Composers)
                </li>
                <li className={classes.liArticle}>GMR (Global Music Rights)</li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    These companies collect and dispense the royalties on
                    composition rights.
                  </>,
                  <>
                    Estas empresas recaudan y distribuyen las regalías sobre
                    derechos de composición.
                  </>,
                  <>
                    Ces sociétés perçoivent et distribuent les redevances sur
                    droits de composition.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이들
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      회사는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작곡권에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      로열티를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      징수하고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      분배합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    <span className={classes.spanEmphasis}>
                      Here’s the problem:
                    </span>
                    &nbsp;Spotify only covers your personal listening rights. It
                    does not pay the higher business music streaming rights fees
                    needed to play in public.
                  </>,
                  <>
                    <span className={classes.spanEmphasis}>
                      Aquí está el problema:
                    </span>
                    &nbsp;Spotify sólo cubre tus derechos de escucha personales.
                    Él no paga las tarifas comerciales más altas por derechos de
                    transmisión de música necesario para jugar en público.
                  </>,
                  <>
                    <span className={classes.spanEmphasis}>
                      Voici le problème&nbsp;:
                    </span>
                    &nbsp;Spotify ne couvre que vos droits d'écoute personnels.
                    Il ne paie pas les frais de droits de diffusion de musique
                    commerciale plus élevés il fallait jouer en public.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      문제는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다음과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      같습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      귀하의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      개인적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      청취
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      권리만
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보장합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공개적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재생하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      데
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      필요한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      높은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비즈니스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트리밍
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      권리
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비용을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지불하지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Spotify can only compensate artists a microfinancial amount
                    per stream for individual listeners at home, but cannot
                    fully compensate artists for large audiences of 50 or 100
                    listeners in a business environment.
                  </>,
                  <>
                    Spotify sólo puede compensar a los artistas con una cantidad
                    microfinanciera por transmisión para oyentes individuales en
                    casa, pero no puede compensar a los artistas por grandes
                    audiencias de 50 o 100 oyentes en un entorno empresarial.
                  </>,
                  <>
                    Spotify ne peut rémunérer les artistes qu'un montant
                    microfinancier par flux pour les auditeurs individuels à la
                    maison, mais ne peut pas entièrement rémunérer les artistes
                    pour de larges audiences de 50 ou 100 auditeurs dans un
                    environnement d'affaires.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아티스트에게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      개별
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      청취자에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트림당
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소액
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      금융
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      금액만
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보상할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있지만,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비즈니스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      환경에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      50~100명의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      청취자가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대규모
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      청중에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대해서는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아티스트에게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전액
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보상할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      없습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisor} />
            <section id={'music-licensing-by-square-footage'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>Do You Actually Need a License?</>,
                  <>¿Realmente necesita una licencia?</>,
                  <>Avez-vous réellement besoin d’une licence&nbsp;?</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실제로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이센스가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      필요합니까?
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Most businesses do. License requirements depend on your
                    square footage:
                  </>,
                  <>
                    La mayoría de las empresas lo hacen. Los requisitos de
                    licencia dependen de su plaza. imágenes:
                  </>,
                  <>
                    La plupart des entreprises le font. Les conditions de
                    licence dépendent de votre place images:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대부분의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기업이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그렇습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이센스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      요구사항은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      면적에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따라
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다릅니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      Food/drink establishments: 3,750+ sq ft requires licensing
                    </>,
                    <>
                      Establecimientos de alimentos/bebidas: más de 3750 pies
                      cuadrados requieren licencia
                    </>,
                    <>
                      Établissements de restauration&nbsp;: plus de
                      3&nbsp;750&nbsp;pieds carrés nécessitent une licence
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음식/음료
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        시설:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        3,750평방피트
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이상에는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        면허
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        필요
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      Other retail businesses: 2,000+ sq ft requires licensing
                    </>,
                    <>
                      Otros negocios minoristas: más de 2000 pies cuadrados
                      requieren licencia
                    </>,
                    <>
                      Autres commerces de détail&nbsp;: plus de
                      2&nbsp;000&nbsp;pieds carrés nécessitent une licence
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기타
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소매업:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        2,000평방피트
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이상에는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이센스가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        필요합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>Limited exemptions exist for:</>,
                  <>Existen exenciones limitadas para:</>,
                  <>Des exemptions limitées existent pour&nbsp;:</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다음에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대해서는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제한적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      면제가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적용됩니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      AM/FM radio with six or fewer speakers (max four per room)
                    </>,
                    <>
                      Radio AM/FM con seis o menos parlantes (máximo cuatro por
                      habitación)
                    </>,
                    <>
                      Radio AM/FM avec six haut-parleurs ou moins (maximum
                      quatre par pièce)
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스피커가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        6개
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이하인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        AM/FM
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라디오(방당
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        최대
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        4개)
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Public domain music only (pre-1922)</>,
                    <>Solo música de dominio público (anterior a 1922)</>,
                    <>Musique du domaine public uniquement (avant 1922)</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        공개
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        도메인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악만
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        해당(1922년
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이전)
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    If you’re using Spotify, YouTube, or Apple Music in your
                    business, exemptions don’t apply&nbsp;— you need proper
                    legal music for commercial use solutions.
                  </>,
                  <>
                    Si estás usando Spotify, YouTube o Apple Music en tu
                    negocios, las exenciones no se aplican; necesita información
                    legal adecuada Soluciones de música para uso comercial.
                  </>,
                  <>
                    Si vous utilisez Spotify, YouTube ou Apple Music dans votre
                    entreprise, les exemptions ne s'appliquent pas&nbsp;: vous
                    avez besoin d'une musique légale appropriée pour les
                    solutions à usage commercial.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비즈니스에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      YouTube
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      또는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Apple
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Music을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      경우에는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      면제가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적용되지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상업적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      솔루션을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      위해서는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적절한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합법적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      필요합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id={'legal-alternatives-to-use'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>Legal Alternatives: What to Use Instead</>,
                  <>Alternativas legales: qué utilizar en su lugar</>,
                  <>Alternatives légales&nbsp;: par quoi utiliser à la place</>,
                  <>
                    <span
                      aria-hidden={true}
                      className={classes.asianTextBlock}
                    ></span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id={'comparison-of-background-music-providers'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      Commercial use of Spotify alternatives is legal and
                      affordable:
                    </>,
                    <>
                      El uso comercial de las alternativas de Spotify es legal y
                      asequible:
                    </>,
                    <>
                      L'utilisation commerciale des alternatives Spotify est
                      légale et abordable:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        법적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        대안:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        대신
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사용할
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <div className={classes.dataTableWrapper}>
                  <table className={classes.dataTable}>
                    <caption className={classes.dtCaption}>
                      {translate(
                        <>
                          Comparison of Business Background Music Providers by
                          Price and Most Suitable Venues
                        </>,
                        <>
                          Comparación de perfiles de empresas Comparación de
                          proveedores de música en función del precio y la
                          ubicación más adecuada
                        </>,
                        <>
                          Comparaison des profils d'entreprises Comparaison des
                          fournisseurs de musique en fonction du prix et de
                          l'emplacement le plus approprié
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            사업 배경 비교 가격 및 가장 적합한 장소별 음악
                            제공업체 비교
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col" className={classes.dataTableHeader}>
                          {translate(
                            <>Service</>,
                            <>Servicio</>,
                            <>Service</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                서비스
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </th>
                        <th scope="col" className={classes.dataTableHeader}>
                          {translate(
                            <>Monthly&nbsp;Cost</>,
                            <>Cuota&nbsp;Mensual</>,
                            <>Frais&nbsp;Mensuels</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                제품
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </th>
                        <th scope="col" className={classes.dataTableHeader}>
                          {translate(
                            <>Best&nbsp;For</>,
                            <>La&nbsp;Mejor&nbsp;Para:</>,
                            <>Idéal&nbsp;Pour</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                가장
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                적합한
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                대상:
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className={classes.dataTableField}>
                          <a
                            className={classes.aHref}
                            target="_blank"
                            href={translate(
                              MAIN_SITE_ENVIRONMENT_HOST,
                              MAIN_SITE_ENVIRONMENT_HOST_ES,
                              MAIN_SITE_ENVIRONMENT_HOST_FR,
                              MAIN_SITE_ENVIRONMENT_HOST_KO,
                              selectedLanguage
                            )}
                            title={translate(
                              'The least expensive, full-featured service for licensed commercial background music.',
                              'Este es el servicio más asequible y a la vez más completo entre los proveedores de servicios de música ambiental comercial con licencia.',
                              "Il s'agit du service le plus abordable et pourtant le plus complet parmi les fournisseurs de services de musique d'ambiance commerciale sous licence.",
                              '라이선스가 부여된 상업용 배경 음악 서비스를 제공하는 업체 중 가장 저렴하면서도 모든 기능을 갖춘 서비스입니다.',
                              selectedLanguage
                            )}
                            rel="noopener noreferrer"
                          >
                            <span translate="no">
                              Practical&nbsp;
                              <span className={classes.spanPre}>
                                Stream&nbsp;
                                <IconExternalLink
                                  size={
                                    width < 500 ? 17 : width < 920 ? 20 : 24
                                  }
                                  width={
                                    width < 500 ? 17 : width < 920 ? 20 : 24
                                  }
                                  height={
                                    width < 500 ? 17 : width < 920 ? 20 : 24
                                  }
                                />
                              </span>
                            </span>
                          </a>{' '}
                        </td>
                        <td className={classes.dataTableField}>
                          <span className={classes.spanGood}>
                            {translate(
                              <>$8.34&nbsp;per&nbsp;zone</>,
                              <>$8.34&nbsp;por&nbsp;zona</>,
                              <>8,34&nbsp;$&nbsp;par&nbsp;zone</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  위치당
                                </span>{' '}
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  $8.34
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </span>
                        </td>
                        <td className={classes.dataTableField}>
                          <span className={classes.spanGood}>
                            {translate(
                              <>All</>,
                              <>Toda</>,
                              <>Toute</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  모두
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>
                          Soundtrack&nbsp;Your&nbsp;Brand
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>$25-50</>,
                            <>$25-50</>,
                            <>25-50&nbsp;$</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                $25-50
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Retail,&nbsp;Restaurants</>,
                            <>Comercio Minorista, Restaurantes</>,
                            <>Commerce&nbsp;de Détail, Restaurants</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                소매,
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                레스토랑
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>
                          Cloud&nbsp;Cover&nbsp;Music
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>$17-35</>,
                            <>$17-35</>,
                            <>17-35&nbsp;$</>,
                            <>$17-35</>,
                            selectedLanguage
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Stores</>,
                            <>Tiendas</>,
                            <>Magasins</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                가게
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>Rockbot</td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>$24-30</>,
                            <>$24-30</>,
                            <>24-30&nbsp;$</>,
                            <>$24-30</>,
                            selectedLanguage
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Bars,&nbsp;Venues</>,
                            <>Tabernas</>,
                            <>Bars,&nbsp;Venues</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                선술집
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                        <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                        <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    All commercial music streaming services provide full legal
                    music for commercial use, copyright protection, and no ads.
                  </>,
                  <>
                    Todos los servicios comerciales de transmisión de música
                    brindan total legalidad. Música para uso comercial,
                    protección de derechos de autor y sin publicidad.
                  </>,
                  <>
                    Tous les services commerciaux de streaming musical offrent
                    des musique à usage commercial, protection des droits
                    d'auteur et sans publicité.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상업용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트리밍
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      서비스는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상업적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      저작권
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보호
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      및
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      광고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      없는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      완전한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합법적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제공합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <span className={classes.subSection}>
                {translate(
                  <>The Bottom Line</>,
                  <>El Punto Principal</>,
                  <>Le Point Principal</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      주요
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      포인트
                    </span>
                  </>,
                  selectedLanguage
                )}
              </span>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>Illegal use: $11/month + fines up to $7.5 million</>,
                    <>Uso Ilegal: $11/mes + multas de hasta $7,5 millones</>,
                    <>
                      Utilisation illégale&nbsp;: 11&nbsp;$/mois + amendes
                      pouvant aller jusqu'à 7,5 millions de dollars
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        불법
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사용:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        월
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        $11
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        +
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        최대
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        $750만
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        벌금
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Legal compliance: $8–60/month with zero risk</>,
                    <>Cumplimiento legal: $8–60/mes sin riesgo</>,
                    <>
                      Conformité légale&nbsp;: 8 à 60&nbsp;$/mois sans risque
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        법적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        준수:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        위험이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전혀
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        없는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        월
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        $8–60
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    A Spotify business license doesn’t exist. Proper business
                    music streaming rights protect your business.
                  </>,
                  <>
                    No existe una licencia comercial de Spotify. Negocio
                    adecuado Los derechos de transmisión de música protegen su
                    negocio.
                  </>,
                  <>
                    Il n’existe pas de licence commerciale Spotify. Les droits
                    de diffusion de musique protègent votre entreprise.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비즈니스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      존재하지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적절한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비즈니스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트리밍
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      권리는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      귀하의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비즈니스를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보호합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Stop using consumer streaming today. Go for legal, licensed
                    platforms.
                  </>,
                  <>
                    Deje de utilizar la transmisión por secuencias para
                    consumidores hoy. Elija legal y con licencia plataformas.
                  </>,
                  <>
                    Arrêtez d’utiliser le streaming grand public dès
                    aujourd’hui. Optez pour un produit légal et sous licence
                    plates-formes.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      오늘부터
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소비자
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트리밍
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      중단하세요.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합법적이고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이센스가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      플랫폼을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      선택하세요.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisorBottomOfArticle} />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
            <br />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <ReadyToTransform
              selectedLanguage={selectedLanguage}
              linkVariant={false}
            />
          </Grid.Col>
          <Grid.Col md={24}>
            <div>
              <br />
              <br />
              <br />
            </div>
          </Grid.Col>
        </Grid>
      </div>
    </div>
  )
}
